/**
 * Reiter „Angebote" — unveraendert uebernommen, nur in eine eigene Datei
 * gezogen. Geladen wird weiterhin erst beim ersten Oeffnen: die Komponente
 * haengt nur am DOM, solange der Reiter aktiv ist.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../Button';
import type { Quote } from '../../types';
import { fetchQuotes } from '../../services/crmService';
import {
    computeQuoteStats, monthlyQuoteVolume, quoteFunnel, topCustomersByQuotes,
    volumeByAssignment, volumeBySection,
} from '../../services/quoteStats';
import { QuoteKpiBar } from '../crm/QuoteKpiBar';
import { MonthlyVolumeChart, QuoteFunnelChart, TopCustomersTable, VolumeBreakdownChart } from '../crm/QuoteCharts';
import type { PeriodKind } from './cockpitUi';

const KIND_LABEL: Record<PeriodKind, string> = { month: 'Monat', quarter: 'Quartal', year: 'Jahr' };

export const QuotesTab: React.FC<{ periodKind: PeriodKind }> = ({ periodKind }) => {
    const [quotes, setQuotes] = useState<Quote[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setQuotes(await fetchQuotes());
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Angebote konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Nach einem Fehler nicht endlos neu versuchen — dafuer gibt es den Knopf.
        if (quotes === null && !loading && !error) load();
    }, [quotes, loading, error, load]);

    // Das Angebotsfenster liegt bewusst auf HEUTE und nicht auf dem gewaehlten
    // Geschaeftsjahr: der Reiter beantwortet „was ist gerade draussen".
    const window = useMemo(() => {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        if (periodKind === 'month') return { today, from: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`, to: today };
        if (periodKind === 'quarter') return { today, from: `${now.getFullYear()}-${pad(Math.floor(now.getMonth() / 3) * 3 + 1)}-01`, to: today };
        return { today, from: `${now.getFullYear()}-01-01`, to: today };
    }, [periodKind]);

    const data = useMemo(() => {
        const list = quotes ?? [];
        return {
            stats: computeQuoteStats(list, window),
            // Der Verlauf ignoriert das Fenster bewusst: bei „Monat" waere sonst
            // genau ein Balken zu sehen. quoteStats deckelt auf 24 Monate.
            monthly: monthlyQuoteVolume(list, { today: window.today }),
            funnel: quoteFunnel(list, window),
            top: topCustomersByQuotes(list, { ...window, limit: 10 }),
            byAssignment: volumeByAssignment(list, window),
            bySection: volumeBySection(list, window),
        };
    }, [quotes, window]);

    if (loading) {
        return (
            <div data-glass="cockpit-panel" className="py-24 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
                <Loader2 className="w-7 h-7 animate-spin mx-auto mb-3 text-blue-500" />
                <p className="text-sm font-medium">Angebote werden geladen…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div data-glass="cockpit-panel" className="py-16 text-center bg-white rounded-2xl border border-red-200">
                <AlertTriangle className="w-7 h-7 text-red-500 mx-auto mb-3" />
                <p className="font-semibold text-slate-700 text-sm">Angebote konnten nicht geladen werden</p>
                <p className="text-[11px] text-slate-500 mt-1 mb-4">{error}</p>
                <Button variant="secondary" onClick={load} icon={<RefreshCw className="w-4 h-4" />}>Erneut versuchen</Button>
            </div>
        );
    }

    return (
        <div data-glass-view="cockpit" className="space-y-5">
            <p data-glass="auf-fenster" className="text-[11px] text-slate-400">
                Angebotsfenster: laufendes Datum ({KIND_LABEL[periodKind]}), unabhängig vom gewählten Geschäftsjahr.
            </p>
            <QuoteKpiBar stats={data.stats} periodLabel={KIND_LABEL[periodKind]} />
            <MonthlyVolumeChart points={data.monthly} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                <VolumeBreakdownChart rows={data.byAssignment} title="Volumen nach Leistungsart" hint="netto je Position, ohne NEP" />
                <VolumeBreakdownChart rows={data.bySection} title="Volumen nach LV-Abschnitt" hint="Abschnitte gleichen Namens zusammengefasst" />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                <QuoteFunnelChart funnel={data.funnel} />
                <TopCustomersTable rows={data.top} />
            </div>
        </div>
    );
};
